import { Type, Static, NumberOptions } from '@sinclair/typebox'
import { MultiPolygon } from "geojson"
import { MultiPolygonTB} from './geojson'
import { PropertyTypeFurtherProcessorTB, type PropertyTypeT } from './property'

import { TRADING_REGION_EMISSIONS_FACTOR } from '@ruminati/constants/gaf/shared'
import { HERBICIDE_OPTIONS, HERBICIDE_PESTICIDE_OPTIONS } from '@ruminati/constants/chemical'
import { OptionalNullableCleanString } from './utils/shared-types'

export enum OtherNitrogenFertiliserType {
  MAP = "monoammonium-phosphate",
  DAP = "diammonium-phosphate",
  UAN = "urea-ammonium-nitrate",
  AN = "ammonium-nitrate",
  CAN = "calcium-ammonium-nitrate"
}

// TODO: replace enum above with this
export const OtherNFertiliserTypeMAPTB = Type.Literal('monoammonium-phosphate')
export const OtherNFertiliserTypeDAPTB = Type.Literal('diammonium-phosphate')
export const OtherNFertiliserTypeUANTB = Type.Literal('urea-ammonium-nitrate')
export const OtherNFertiliserTypeANTB = Type.Literal('ammonium-nitrate')
export const OtherNFertiliserTypeCANTB = Type.Literal('calcium-ammonium-nitrate')

export const OtherNFertilisersTypeTB = Type.Union([
  OtherNFertiliserTypeMAPTB,
  OtherNFertiliserTypeDAPTB,
  OtherNFertiliserTypeUANTB,
  OtherNFertiliserTypeANTB,
  OtherNFertiliserTypeCANTB
])
export type OtherNFertilisersTypeT = Static<typeof OtherNFertilisersTypeTB>

export const OtherNFertilisersTypeMapTB = Type.Object({
  'Monoammonium Phosphate (MAP)': OtherNFertiliserTypeMAPTB,
  'Diammonium Phosphate (DAP)': OtherNFertiliserTypeDAPTB,
  'Urea Ammonium Nitrate (UAN)': OtherNFertiliserTypeUANTB,
  'Ammonium Nitrate': OtherNFertiliserTypeANTB,
  'Calcium Ammonium Nitrate (CAN)': OtherNFertiliserTypeCANTB
})
export type OtherNFertilisersTypeMapT = Static<typeof OtherNFertilisersTypeMapTB>

export type Trees = {
  reportId?: string
  species: number
  area: number
  age: number
  soilType?: number
  geom?: MultiPolygon
  propertyId?: string
}

export type Pasture = {
  ureaDrylandPasture: number
  ureaDrylandCrops: number
  ureaIrrigatedPasture: number
  ureaIrrigatedCrops: number
  singleSuperphosphate: number
  lime: number
  fractionLime: number
  otherNFertilisers: OtherNFertilisersWithSystemPasture[]
  herbicidePesticide: number
  herbicide: number
  herbicidesPesticidesUsed: HerbicideOrPesticideUse[]
}

export type OtherNFertilisers = {
  type: OtherNitrogenFertiliserType
  amountUsed: number
}

export type OtherNFertilisersWithSystemPasture = OtherNFertilisers & {
  productionType: 'dryland' | 'irrigated'
}

export type FertiliserUse = {
  id: number
  rateApplied: number
}

export type HerbicideOrPesticideUse = {
  id: string
  rateApplied: number
  areaAppliedHectares: number
}

export const Pasture_PreV3_5TB = Type.Object({
  urea: NumberMinZeroDefaultUndefinedTB(),
  lime: NumberMinZeroDefaultUndefinedTB(),
  singleSuperphosphate: NumberMinZeroDefaultUndefinedTB(),
  pesticide: NumberMinZeroDefaultUndefinedTB(),
  otherNitrogen: NumberMinZeroDefaultUndefinedTB(),
  glyphosate: NumberMinZeroDefaultUndefinedTB(),
})
export type Pasture_PreV3_5T = Static<typeof Pasture_PreV3_5TB>

export const PastureUreaDetailedTypeDrylandPastureTB = Type.Literal('drylandPasture')
export const PastureUreaDetailedTypeDrylandCropsTB = Type.Literal('drylandCrops')
export const PastureUreaDetailedTypeIrrigatedPastureTB = Type.Literal('irrigatedPasture')
export const PastureUreaDetailedTypeIrrigatedCropsTB = Type.Literal('irrigatedCrops')

export const PastureUreaDetailedTypeTB = Type.Union([
  PastureUreaDetailedTypeDrylandPastureTB,
  PastureUreaDetailedTypeDrylandCropsTB,
  PastureUreaDetailedTypeIrrigatedPastureTB,
  PastureUreaDetailedTypeIrrigatedCropsTB
])
export type PastureUreaDetailedTypeT = Static<typeof PastureUreaDetailedTypeTB>

export const PastureUreaDetailedTypeMapTB = Type.Object({
  'Dryland Pasture': PastureUreaDetailedTypeDrylandPastureTB,
  'Dryland Fodder Crops': PastureUreaDetailedTypeDrylandCropsTB,
  'Irrigated Pasture': PastureUreaDetailedTypeIrrigatedPastureTB,
  'Irrigated Fodder Crops': PastureUreaDetailedTypeIrrigatedCropsTB,
})
export type PastureUreaDetailedTypeMapT = Static<typeof PastureUreaDetailedTypeMapTB>

export const PastureOtherNFertiliserProductionTypeIrrigatedTB = Type.Literal('irrigated')
export const PastureOtherNFertiliserProductionTypeDrylandTB = Type.Literal('dryland')

export const PastureOtherNFertiliserProductionTypeTB = Type.Union([
  PastureOtherNFertiliserProductionTypeIrrigatedTB,
  PastureOtherNFertiliserProductionTypeDrylandTB
])
export type PastureOtherNFertiliserProductionTypeT = Static<typeof PastureOtherNFertiliserProductionTypeTB>

export const PastureOtherNFertiliserProductionTypeMapTB = Type.Object({
  'Irrigated': PastureOtherNFertiliserProductionTypeIrrigatedTB,
  'Dryland': PastureOtherNFertiliserProductionTypeDrylandTB,
})
export type PastureOtherNFertiliserProductionTypeMapT = Static<typeof PastureOtherNFertiliserProductionTypeMapTB>

// N.B. not itself an array because we want to use an array with minItems for the modal, see under modals
export const HerbicidePesticideUsedTB = Type.Object({
  id: Type.Union([
    ...HERBICIDE_OPTIONS.map(r => Type.Literal(r.id)),
    ...HERBICIDE_PESTICIDE_OPTIONS.map(r => Type.Literal(r.id)),
  ], {
    default: undefined
  }),
  rateApplied: NumberMinZeroDefaultUndefinedTB(),
  areaAppliedHectares: NumberMinZeroDefaultUndefinedTB()
})
export type HerbicidePesticideUsedT = Static<typeof HerbicidePesticideUsedTB>

export const Pasture_PostV3_4RecordTB = Type.Object({
  ureaDrylandPasture: NumberMinZeroDefaultUndefinedTB(),
  ureaDrylandCrops: NumberMinZeroDefaultUndefinedTB(),
  ureaIrrigatedPasture: NumberMinZeroDefaultUndefinedTB(),
  ureaIrrigatedCrops: NumberMinZeroDefaultUndefinedTB(),
  lime: NumberMinZeroDefaultUndefinedTB(),
  fractionLime: NumberMinZeroDefaultUndefinedTB({ maximum: 1 }),
  singleSuperphosphate: NumberMinZeroDefaultUndefinedTB(),
  herbicidesPesticidesUsed: Type.Array(HerbicidePesticideUsedTB),
  herbicide: NumberMinZeroDefaultUndefinedTB(),
  herbicidePesticide: NumberMinZeroDefaultUndefinedTB(),
  otherNFertilisers: Type.Optional(Type.Array(Type.Object({
    type: OtherNFertilisersTypeTB,
    amountUsed: NumberMinZeroDefaultUndefinedTB(),
    productionType: PastureOtherNFertiliserProductionTypeTB
  })))
})
export type Pasture_PostV3_4RecordT = Static<typeof Pasture_PostV3_4RecordTB>

export const Pasture_PostV3_4FormTB = Type.Object({
  ureaDetailed: Type.Object({
    drylandPasture: NumberMinZeroDefaultUndefinedTB(),
    drylandCrops: NumberMinZeroDefaultUndefinedTB(),
    irrigatedPasture: NumberMinZeroDefaultUndefinedTB(),
    irrigatedCrops: NumberMinZeroDefaultUndefinedTB()
  }),
  lime: NumberMinZeroDefaultUndefinedTB(),
  fractionLime: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  singleSuperphosphate: NumberMinZeroDefaultUndefinedTB(),
  herbicidesPesticidesUsed: Type.Array(HerbicidePesticideUsedTB),
  herbicide: NumberMinZeroDefaultUndefinedTB(),
  herbicidePesticide: NumberMinZeroDefaultUndefinedTB(),
  otherNFertilisers: Type.Optional(Type.Array(Type.Object({
    type: OtherNFertilisersTypeTB,
    amountUsed: NumberMinZeroDefaultUndefinedTB(),
    productionType: PastureOtherNFertiliserProductionTypeTB
  }), { default: [] }))
})
export type Pasture_PostV3_4FormT = Static<typeof Pasture_PostV3_4FormTB>

// transform functions from db -> client form and client form -> db
export function transformPasturePost3_4RecordToForm(record: Pasture_PostV3_4RecordT): Pasture_PostV3_4FormT {
  return {
    ...record,
    ureaDetailed: {
      drylandPasture: record.ureaDrylandPasture,
      drylandCrops: record.ureaDrylandCrops,
      irrigatedPasture: record.ureaIrrigatedPasture,
      irrigatedCrops: record.ureaIrrigatedCrops,
    },
    fractionLime: record.fractionLime * 100 // decimal -> percentage
  };
}
export function transformPasturePost3_4FormToRecord(form: Pasture_PostV3_4FormT): Pasture_PostV3_4RecordT {
  return {
    ...form,
    otherNFertilisers: form.otherNFertilisers ?? [],
    ureaDrylandPasture: form.ureaDetailed.drylandPasture,
    ureaDrylandCrops: form.ureaDetailed.drylandCrops,
    ureaIrrigatedPasture: form.ureaDetailed.irrigatedPasture,
    ureaIrrigatedCrops: form.ureaDetailed.irrigatedCrops,
    fractionLime: form.fractionLime / 100 // percentage -> decimal
  };
}

export const ConsumablesFormTB = Type.Object({
  petrol: NumberMinZeroDefaultUndefinedTB(),
  diesel: NumberMinZeroDefaultUndefinedTB(),
  electricityUsage: NumberMinZeroDefaultUndefinedTB(),
  percentRenewables: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
})
export type ConsumablesFormT = Static<typeof ConsumablesFormTB>

export const ConsumablesRecordTB = Type.Object({
  petrol: NumberMinZeroDefaultUndefinedTB(),
  diesel: NumberMinZeroDefaultUndefinedTB(),
  electricityUsage: NumberMinZeroDefaultUndefinedTB(),
  percentRenewables: NumberMinZeroDefaultUndefinedTB({ maximum: 1 }),
})
export type ConsumablesRecordT = Static<typeof ConsumablesRecordTB>

// transform functions from db -> client form and client form -> db
export function transformConsumablesRecordToForm(record: ConsumablesRecordT): ConsumablesFormT {
  return {
    ...record,
    percentRenewables: record.percentRenewables * 100, // decimal -> percentage
  };
}
export function transformConsumablesFormToRecord(form: ConsumablesFormT): ConsumablesRecordT {
  return {
    ...form,
    percentRenewables: form.percentRenewables / 100, // percentage -> decimal
  };
}

export const CattlePurchaseTB = Type.Object({
  name: OptionalNullableCleanString,
  origin: Type.Union(
    TRADING_REGION_EMISSIONS_FACTOR.map(r => Type.Literal(r.originCode))
  ),
  averageWeightPurchased: NumberMinZeroDefaultUndefinedTB({ maximum: 1500 }),
  numberPurchased: NumberMinZeroDefaultUndefinedTB({minimum: 1}),
})
export type CattlePurchaseT = Static<typeof CattlePurchaseTB>

export const CattlePurchasesTB = Type.Array(CattlePurchaseTB)
export type CattlePurchasesT = Static<typeof CattlePurchasesTB>

// transform function specifically for managing different property types
export function transformCattlePurchasesFormToRecord(form: CattlePurchasesT, propertyType: PropertyTypeT): CattlePurchasesT {
  return form.map((p) => ({
    ...p,
    averageWeightPurchased: propertyType === PropertyTypeFurtherProcessorTB.const ? p.averageWeightPurchased * 1000 : p.averageWeightPurchased,
  }));
}
export function transformCattlePurchasesRecordToForm(record: CattlePurchasesT, propertyType: PropertyTypeT): CattlePurchasesT {
  return record.map((p) => ({
    ...p,
    averageWeightPurchased: propertyType === PropertyTypeFurtherProcessorTB.const ? p.averageWeightPurchased / 1000 : p.averageWeightPurchased,
  }));
}

export const SheepPurchasesFormTB = Type.Array(Type.Object({
  name: OptionalNullableCleanString,
  averageWeightPurchased: NumberMinZeroDefaultUndefinedTB({ maximum: 150 }),
  numberPurchased: NumberMinZeroDefaultUndefinedTB({minimum: 1}),
  proportionMerino: NumberMinZeroDefaultUndefinedTB({ maximum: 100 })
}))
export type SheepPurchasesFormT = Static<typeof SheepPurchasesFormTB>

export const SheepPurchaseRecordTB = Type.Object({
  name: OptionalNullableCleanString,
  averageWeightPurchased: NumberMinZeroDefaultUndefinedTB({ maximum: 150 }),
  numberPurchased: NumberMinZeroDefaultUndefinedTB({minimum: 1}),
  proportionMerino: NumberMinZeroDefaultUndefinedTB({ maximum: 1 })
})
export type SheepPurchaseRecordT = Static<typeof SheepPurchaseRecordTB>
export const SheepPurchasesRecordTB = Type.Array(SheepPurchaseRecordTB)
export type SheepPurchasesRecordT = Static<typeof SheepPurchasesRecordTB>

// transform functions from db -> client form and client form -> db
export function transformSheepPurchasesRecordToForm(record: SheepPurchasesRecordT, propertyType: PropertyTypeT): SheepPurchasesFormT {
  return record.map((item) => ({
    ...item,
    averageWeightPurchased: propertyType === PropertyTypeFurtherProcessorTB.const ? item.averageWeightPurchased / 1000 : item.averageWeightPurchased,
    proportionMerino: item.proportionMerino * 100, // decimal -> percentage
  }));
}
export function transformSheepPurchasesFormToRecord(form: SheepPurchasesFormT, propertyType: PropertyTypeT): SheepPurchasesRecordT {
  return form.map((item) => ({
    ...item,
    averageWeightPurchased: propertyType === PropertyTypeFurtherProcessorTB.const ? item.averageWeightPurchased * 1000 : item.averageWeightPurchased,
    proportionMerino: item.proportionMerino / 100, // percentage -> decimal
  }));
}

export const SupplementaryFeedTB = Type.Object({
  grain: NumberMinZeroDefaultUndefinedTB(),
  cottonSeed: NumberMinZeroDefaultUndefinedTB(),
  hay: NumberMinZeroDefaultUndefinedTB()
})
export type SupplementaryFeedT = Static<typeof SupplementaryFeedTB>

export const MineralSupplementTypeMineralBlockTB = Type.Literal('mineral-block')
export const MineralSupplementTypeWeanerBlockTB = Type.Literal('weaner-block')
export const MineralSupplementTypeDrySeasonMixTB = Type.Literal('dry-season-mix')

export const MineralSupplementTypeTB = Type.Union([
  MineralSupplementTypeMineralBlockTB,
  MineralSupplementTypeWeanerBlockTB,
  MineralSupplementTypeDrySeasonMixTB
])
export type MineralSupplementTypeT = Static<typeof MineralSupplementTypeTB>

export const MineralSupplementTypeMapTB = Type.Object({
  'Mineral Block': MineralSupplementTypeMineralBlockTB,
  'Weaner Block': MineralSupplementTypeWeanerBlockTB,
  'Dry Season Mix': MineralSupplementTypeDrySeasonMixTB
})
export type MineralSupplementTypeMapT = Static<typeof MineralSupplementTypeMapTB>

export const MineralSupplementFormTB = Type.Array(Type.Object({
  supplementType: MineralSupplementTypeTB,
  tonnesOfProduct: NumberMinZeroDefaultUndefinedTB(),
  proportionUrea: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
}))
export type MineralSupplementFormT = Static<typeof MineralSupplementFormTB>

export const MineralSupplementRecordTB = Type.Array(Type.Object({
  supplementType: MineralSupplementTypeTB,
  tonnesOfProduct: NumberMinZeroDefaultUndefinedTB(),
  proportionUrea: NumberMinZeroDefaultUndefinedTB({ maximum: 1 }),
}))
export type MineralSupplementRecordT = Static<typeof MineralSupplementRecordTB>

// transform functions from db -> client form and client form -> db
export function transformMineralSupplementRecordToForm(record: MineralSupplementRecordT): MineralSupplementFormT {
  return record.map((item) => ({
    ...item,
    proportionUrea: item.proportionUrea * 100, // decimal -> percentage
  }));
}
export function transformMineralSupplementFormToRecord(form: MineralSupplementFormT): MineralSupplementRecordT {
  return form.map((item) => ({
    ...item,
    proportionUrea: item.proportionUrea / 100, // percentage -> decimal
  }));
}

// special type for SupplementaryFeed form, given it's composed of SupplementaryFeed and MineralSupplement
export const SupplementaryFeedFormTB = Type.Composite([
  SupplementaryFeedTB,
  Type.Object({
    mineralSupplements: Type.Optional(MineralSupplementFormTB),
  })
])
export type SupplementaryFeedFormT = Static<typeof SupplementaryFeedFormTB>

// export const GrainAllocationTB = Type.Object({
//   grainRecordStableId: Type.String(),
//   allocation: Type.Number({
//     minimum: 0,
//     maximum: 1
//   })
// })

// export const ConsumablesAllocationTB = Type.Object({
//   cattle: Type.Optional(Type.Number({
//     minimum: 0,
//     maximim: 1
//   })),
//   sheep: Type.Optional(Type.Number({
//     minimum: 0,
//     maximim: 1
//   })),
//   grain: Type.Optional(Type.Number({
//     minimum: 0,
//     maximim: 1
//   })),
//   wool: Type.Optional(Type.Number({
//     minimum: 0,
//     maximim: 1
//   })),
//   grainAllocations: Type.Optional(Type.Array(GrainAllocationTB)),
// })

// export const ProductionTB = Type.Object({
//   cattleLiveweightSoldKgs: Type.Number({
//     minimum: 0,
//   }),
//   sheepLiveweightSoldKgs: Type.Number({
//     minimum: 0,
//   }),
//   cleanWoolYieldPercentage: Type.Number({
//     minimum: 0,
//     maximum: 1
//   }),
//   greasyWoolKgs: Type.Number({
//     minimum: 0,
//   }),
//   consumablesAllocation: Type.Object({
//     petrolDiesel: ConsumablesAllocationTB,
//     electricity: ConsumablesAllocationTB
//   })
// })

// export type Production = Static<typeof ProductionTB>

// export const ProductionFormTB = Type.Object({
//   cattleLiveweightSoldKgs: Type.Number({
//     minimum: 0,
//   }),
//   sheepLiveweightSoldKgs: Type.Number({
//     minimum: 0,
//   }),
//   cleanWoolYieldPercentage: Type.Number({
//     minimum: 0,
//     maximum: 1
//   }),
//   greasyWoolKgs: Type.Number({
//     minimum: 0,
//   }),
//   petrolDieselAllocation: ConsumablesAllocationTB,
//   electricityAllocation: ConsumablesAllocationTB
// })
// export type ProductionForm = Static<typeof ProductionFormTB>

// export const ProductionTransformer = Type.Transform(ProductionTB)
//   .Decode((value: Production) => {
//     const out: ProductionForm = {
//       ...value,
//       petrolDieselAllocation: value.consumablesAllocation.petrolDiesel,
//       electricityAllocation: value.consumablesAllocation.electricity
//     }
//     return out
//   })
//   .Encode((value: ProductionForm) => {
//     return {
//       ...value,
//       consumablesAllocation: {
//         electricity: value.electricityAllocation,
//         petrolDiesel: value.petrolDieselAllocation
//       }
//     }
//   })

export function NumberMinZeroDefaultUndefinedTB (options: NumberOptions = {}) {
  return Type.Number({
    minimum: 0,
    default: undefined,
    ...options
  })
}

export const SeasonTypeSpringTB = Type.Literal('spring')
export const SeasonTypeSummerTB = Type.Literal('summer')
export const SeasonTypeAutumnTB = Type.Literal('autumn')
export const SeasonTypeWinterTB = Type.Literal('winter')

export const SeasonTypeTB = Type.Union([
  SeasonTypeSpringTB,
  SeasonTypeSummerTB,
  SeasonTypeAutumnTB,
  SeasonTypeWinterTB
])
export type SeasonTypeT = Static<typeof SeasonTypeTB>

export function SeasonTB (options: NumberOptions = {}) {
  return Type.Record(SeasonTypeTB, NumberMinZeroDefaultUndefinedTB(options))
}
// derive TB and static types of default values from generation function
export const SeasonalValuesDefaultTB = SeasonTB()
export type SeasonalValuesDefaultT = Static<typeof SeasonalValuesDefaultTB>
export const SeasonalValuesDefaultPartialTB = Type.Partial(SeasonTB())
export type SeasonalValuesDefaultPartialT = Static<typeof SeasonalValuesDefaultPartialTB>

export const SeasonMapTB = Type.Object({
  'Spring': SeasonTypeSpringTB,
  'Summer': SeasonTypeSummerTB,
  'Autumn': SeasonTypeAutumnTB,
  'Winter': SeasonTypeWinterTB
})
export type SeasonMapT = Static<typeof SeasonMapTB>

export const TreesTB = Type.Array(Type.Object({
  species: Type.Number(), // TODO: more precisely should be any value literal from REGIONAL_VEGETATION_TYPES
  soilType: Type.Number(), // TODO: more precisely should be any value literal from REGIONAL_SOIL_TYPES
  age: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  area: NumberMinZeroDefaultUndefinedTB(),
  geom: Type.Optional(Type.Union([MultiPolygonTB, Type.Null(), Type.Undefined()]))
}))
export type TreesT = Static<typeof TreesTB>

export const CattleTransportTruckTypeFourDeckTrailerTB = Type.Literal('4-deck-trailer')
export const CattleTransportTruckTypeSixDeckTrailerTB = Type.Literal('6-deck-trailer')
export const CattleTransportTruckTypeBDoubleTB = Type.Literal('b-double')

export const CattleTransportTruckTypeTB  = Type.Union([
  CattleTransportTruckTypeFourDeckTrailerTB,
  CattleTransportTruckTypeSixDeckTrailerTB,
  CattleTransportTruckTypeBDoubleTB
])
export type CattleTransportTruckTypeT = Static<typeof CattleTransportTruckTypeTB>

export const CattleTransportTruckTypeMapTB = Type.Object({
  'Four Deck Trailer': CattleTransportTruckTypeFourDeckTrailerTB,
  'Six Deck Trailer': CattleTransportTruckTypeSixDeckTrailerTB,
  'B-Double': CattleTransportTruckTypeBDoubleTB
})
export type CattleTransportTruckTypeMapT = Static<typeof CattleTransportTruckTypeMapTB>

// modals
export const ConsumablesPetrolDieselUseTB = Type.Object({
  annualBill: NumberMinZeroDefaultUndefinedTB(),
  averagePrice: NumberMinZeroDefaultUndefinedTB()
})
export type ConsumablesPetrolDieselUseT = Static<typeof ConsumablesPetrolDieselUseTB>

export const ConsumablesElectricityUseTB = Type.Object({
  annualBill: NumberMinZeroDefaultUndefinedTB(),
})
export type ConsumablesElectricityUseT = Static<typeof ConsumablesElectricityUseTB>

export const HerbicidesPesticidesUsedTB = Type.Array(HerbicidePesticideUsedTB, { minItems: 1 })
export type HerbicidesPesticidesUsedT = Static<typeof HerbicidesPesticidesUsedTB>

export const CottonBalesTB = Type.Object({
  numberOfBales: NumberMinZeroDefaultUndefinedTB()
})
export type CottonBalesT = Static<typeof CottonBalesTB>
